import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { useT, useFormatDate } from '../i18n';

interface ProgressionPlan {
  progression_type: 'progressive' | 'constant';
  start_pct: number | null;
  target_pct: number;
  period_start: string;
  period_end: string | null;
  target_date: string | null;
  qualitative_objectives: string[] | null;
  source: 'oai_decision' | 'rm_adjustment';
  source_created_at: string | null;
  oai_source_document_id: number | null;
}

interface MeasureCtx {
  measure_id: number;
  participant_id: number;
  participant_first_name: string | null;
  participant_last_name: string | null;
  type_label: string | null;
  contract_number: string | null;
  start_date: string | null;
  end_date: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  ctx: MeasureCtx | null;
  currentPlan: ProgressionPlan | null;
  // Live metrics (for the email preview)
  currentTauxPct: number | null;     // 4-week avg taux %
  presence30dPct: number | null;     // 30-day presence %
  oaiConseillerName?: string | null;
  oaiConseillerEmail?: string | null;
}

/**
 * Date formatter reserved for the generated email letter content below (previewBody /
 * describePlan): the letter is official correspondence sent to French-speaking cantonal
 * contacts and must always render dd.mm.yyyy in French, regardless of the active UI
 * language. Do not swap this for the locale-aware `fd` formatter — UI chrome elsewhere in
 * this modal uses `fd` instead.
 */
function fmtDateFr(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function describePlan(p: {
  progression_type: 'progressive' | 'constant';
  start_pct: number | null;
  target_pct: number;
  target_date: string | null;
}): string {
  if (p.progression_type === 'progressive' && p.start_pct != null) {
    return `Progression ${p.start_pct} % → ${p.target_pct} %${
      p.target_date ? ` d'ici le ${fmtDateFr(p.target_date)}` : ''
    }`;
  }
  return `Taux constant à ${p.target_pct} %${
    p.target_date ? ` d'ici le ${fmtDateFr(p.target_date)}` : ''
  }`;
}

/** Mirror of src/server/services/comms.ts — kept in sync for the live preview. */
function previewBody(args: {
  participant: { first_name: string | null; last_name: string | null };
  measure: { type_label: string | null; contract_number: string | null; start_date: string | null };
  previous: ProgressionPlan | null;
  next: {
    progression_type: 'progressive' | 'constant';
    start_pct: number | null;
    target_pct: number;
    period_start: string;
    target_date: string | null;
    effective_from: string;
  };
  metrics: { current_taux_pct: number | null; presence_30d_pct: number | null };
  rm: { full_name: string | null; email: string | null };
  oai_contact: { full_name: string | null; email: string | null };
  reason: string;
  oai_note?: string | null;
}): { subject: string; body: string } {
  const fullName = [args.participant.first_name, args.participant.last_name]
    .filter(Boolean).join(' ').trim() || '—';
  const firstName = args.participant.first_name || '—';
  const measureLabel = args.measure.type_label || 'mesure';
  const subject = `Ajustement du taux d'activité — ${fullName} — ${measureLabel}`;
  const salutation = args.oai_contact.full_name
    ? `Bonjour ${args.oai_contact.full_name},`
    : 'Madame, Monsieur,';

  const previousSourceLabel = !args.previous
    ? `Plan précédent`
    : args.previous.source === 'oai_decision'
    ? `Mandat AI initial${
        args.previous.source_created_at
          ? ` (${fmtDateFr(args.previous.source_created_at.slice(0, 10))})`
          : ''
      }`
    : `Plan précédent${
        args.previous.source_created_at
          ? ` (ajusté le ${fmtDateFr(args.previous.source_created_at.slice(0, 10))})`
          : ''
      }`;

  const metricsLines: string[] = [];
  if (args.metrics.current_taux_pct != null) {
    metricsLines.push(
      `  • Taux d'activité moyen (4 dernières semaines) : ${Math.round(args.metrics.current_taux_pct)} %`,
    );
  }
  if (args.metrics.presence_30d_pct != null) {
    metricsLines.push(
      `  • Taux de présence (30 jours) : ${Math.round(args.metrics.presence_30d_pct)} %`,
    );
  }
  const metricsBlock = metricsLines.length
    ? `Situation actuelle mesurée :\n${metricsLines.join('\n')}\n\n`
    : '';

  const contractRef = args.measure.contract_number ? `, contrat ${args.measure.contract_number}` : '';
  const startRef = args.measure.start_date ? ` démarrée le ${fmtDateFr(args.measure.start_date)}` : '';
  const oaiNoteBlock = args.oai_note && args.oai_note.trim().length > 0
    ? `${args.oai_note.trim()}\n\n`
    : '';

  const prevLine = args.previous
    ? describePlan({
        progression_type: args.previous.progression_type,
        start_pct: args.previous.start_pct,
        target_pct: args.previous.target_pct,
        target_date: args.previous.target_date,
      })
    : 'Aucun plan précédent enregistré.';

  const body =
`${salutation}

Dans le cadre du suivi de ${fullName} à L'Éveil (mesure ${measureLabel}${startRef}${contractRef}), et après évaluation de l'évolution actuelle, nous souhaitons vous proposer l'ajustement suivant du taux d'activité prévu.

${previousSourceLabel} :
  • ${prevLine}

Nouveau plan proposé (effet au ${fmtDateFr(args.next.effective_from)}) :
  • ${describePlan(args.next)}
  • Période : ${fmtDateFr(args.next.period_start)} → ${fmtDateFr(args.next.target_date)}

${metricsBlock}Motif de l'ajustement :
${args.reason.trim() || '(à compléter)'}

${oaiNoteBlock}Cet ajustement nous semble plus réaliste compte tenu de l'évolution de ${firstName}. Nous restons à votre disposition pour en discuter et confirmer cet ajustement.

Cordialement,
${args.rm.full_name || ''}
Référent Mesure — L'Éveil
${args.rm.email || ''}
`;
  return { subject, body };
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Snap a YYYY-MM-DD date to the Monday of its ISO week.
 *
 * Why: the chart aggregates attendance per ISO week. If an adjustment takes
 * effect mid-week, the affected week would be split between two plans —
 * messy and ambiguous. Forcing Monday means each week is fully covered by
 * exactly one plan.
 */
function snapToMonday(iso: string): string {
  if (!iso) return iso;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  // JS getDay(): Sun=0, Mon=1, ..., Sat=6 → days to subtract to reach Monday.
  const dow = d.getDay();
  const offset = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
}

export function AdjustmentModal({
  open,
  onClose,
  onSaved,
  ctx,
  currentPlan,
  currentTauxPct,
  presence30dPct,
  oaiConseillerName,
  oaiConseillerEmail,
}: Props) {
  const { user } = useAuth();
  const { t } = useT();
  const fd = useFormatDate();

  // Form state — prefilled from currentPlan
  const [progressionType, setProgressionType] = useState<'progressive' | 'constant'>('progressive');
  const [startPct, setStartPct] = useState<string>('');
  const [targetPct, setTargetPct] = useState<string>('');
  const [periodStart, setPeriodStart] = useState<string>('');
  const [targetDate, setTargetDate] = useState<string>('');
  const [qualitativeText, setQualitativeText] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [oaiNote, setOaiNote] = useState<string>('');
  const [effectiveFrom, setEffectiveFrom] = useState<string>(today());
  const [toName, setToName] = useState<string>('');
  const [toEmail, setToEmail] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);

  // Prefill when the modal opens (or measure changes).
  useEffect(() => {
    if (!open || !ctx) return;
    const monday = snapToMonday(today());
    if (currentPlan) {
      setProgressionType(currentPlan.progression_type);
      setStartPct(currentPlan.start_pct != null ? String(currentPlan.start_pct) : '');
      setTargetPct(currentPlan.target_pct != null ? String(currentPlan.target_pct) : '');
      setPeriodStart(monday);
      setTargetDate(currentPlan.target_date || ctx.end_date || '');
      setQualitativeText(
        Array.isArray(currentPlan.qualitative_objectives)
          ? currentPlan.qualitative_objectives.join('\n')
          : '',
      );
    } else {
      setProgressionType('progressive');
      setStartPct('');
      setTargetPct('');
      setPeriodStart(monday);
      setTargetDate(ctx.end_date || '');
      setQualitativeText('');
    }
    setReason('');
    setOaiNote('');
    setEffectiveFrom(monday);
    setToName(oaiConseillerName || '');
    setToEmail(oaiConseillerEmail || '');
    setError(null);
  }, [open, ctx, currentPlan, oaiConseillerName, oaiConseillerEmail]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const preview = useMemo(() => {
    if (!ctx) return { subject: '', body: '' };
    const targetN = Number(targetPct);
    const startN = progressionType === 'progressive' && startPct !== '' ? Number(startPct) : null;
    return previewBody({
      participant: {
        first_name: ctx.participant_first_name,
        last_name: ctx.participant_last_name,
      },
      measure: {
        type_label: ctx.type_label,
        contract_number: ctx.contract_number,
        start_date: ctx.start_date,
      },
      previous: currentPlan,
      next: {
        progression_type: progressionType,
        start_pct: startN,
        target_pct: Number.isFinite(targetN) ? targetN : 0,
        period_start: periodStart || today(),
        target_date: targetDate || null,
        effective_from: effectiveFrom || today(),
      },
      metrics: { current_taux_pct: currentTauxPct, presence_30d_pct: presence30dPct },
      rm: { full_name: user?.name || null, email: user?.email || null },
      oai_contact: { full_name: toName || null, email: toEmail || null },
      reason,
      oai_note: oaiNote,
    });
  }, [
    ctx, currentPlan, progressionType, startPct, targetPct, periodStart, targetDate,
    effectiveFrom, currentTauxPct, presence30dPct, user, toName, toEmail, reason, oaiNote,
  ]);

  if (!open || !ctx) return null;

  const isProgressive = progressionType === 'progressive';
  const reasonOk = reason.trim().length >= 5;
  const numbersOk = Number.isFinite(Number(targetPct))
    && (!isProgressive || (startPct !== '' && Number.isFinite(Number(startPct))));
  const datesOk = !!periodStart;

  const canSubmit = reasonOk && numbersOk && datesOk && !submitting;

  async function handleSubmit() {
    if (!ctx) return;
    setSubmitting(true);
    setError(null);
    try {
      const qualitative_objectives = qualitativeText
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);

      await api.createMeasureAdjustment(ctx.measure_id, {
        progression_type: progressionType,
        start_pct: isProgressive ? Number(startPct) : null,
        target_pct: Number(targetPct),
        period_start: periodStart,
        target_date: targetDate || null,
        qualitative_objectives: qualitative_objectives.length > 0 ? qualitative_objectives : undefined,
        reason: reason.trim(),
        oai_note: oaiNote.trim() || undefined,
        effective_from: effectiveFrom,
        to_name: toName.trim() || undefined,
        to_email: toEmail.trim() || undefined,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.message || t('adjust.errSave'));
    } finally {
      setSubmitting(false);
    }
  }

  const participantFullName =
    `${ctx.participant_last_name || ''} ${ctx.participant_first_name || ''}`.trim() || '—';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 'var(--s16)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          width: 'min(1100px, 100%)',
          maxHeight: '92vh',
          borderRadius: 'var(--r1)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 10px 32px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <header style={{
          padding: 'var(--s12) var(--s16)',
          borderBottom: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 'var(--t-15)', fontWeight: 700 }}>
              {t('adjust.modalTitle')}
            </div>
            <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>
              {participantFullName} · {ctx.type_label || t('adjust.genericMeasure')} · {ctx.start_date ? fd.short(ctx.start_date) : '—'} – {ctx.end_date ? fd.short(ctx.end_date) : '—'}
            </div>
          </div>
          <button onClick={onClose} className="sort-btn" style={{ minWidth: 32 }} title={t('adjust.closeTooltip')}>×</button>
        </header>

        {/* Body */}
        <div style={{ padding: 'var(--s16)', overflowY: 'auto' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 'var(--s16)', marginBottom: 'var(--s16)',
          }}>
            {/* Plan actuel (read-only) */}
            <div style={{
              border: '1px solid var(--line)',
              borderRadius: 'var(--r1)',
              padding: 'var(--s12)',
              background: 'var(--bg)',
            }}>
              <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 'var(--s8)' }}>
                {t('adjust.currentPlanLabel')}
              </div>
              {currentPlan ? (
                <>
                  <Row label={t('adjust.labelType')}>
                    {currentPlan.progression_type === 'progressive' ? t('adjust.typeProgressive') : t('adjust.typeConstant')}
                  </Row>
                  <Row label={t('adjust.labelRate')}>
                    {currentPlan.progression_type === 'progressive' && currentPlan.start_pct != null
                      ? `${currentPlan.start_pct} % → ${currentPlan.target_pct} %`
                      : `${currentPlan.target_pct} %`}
                  </Row>
                  <Row label={t('adjust.labelDeadline')}>{currentPlan.target_date ? fd.short(currentPlan.target_date) : '—'}</Row>
                  <Row label={t('adjust.labelSource')}>
                    {currentPlan.source === 'oai_decision' ? t('adjust.mandatAiLabel') : t('adjust.sourceAdjustmentRm')}
                    {currentPlan.source_created_at && (
                      <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                        ({fd.short(currentPlan.source_created_at.slice(0, 10))})
                      </span>
                    )}
                    {currentPlan.oai_source_document_id && (
                      <a
                        href={api.documentBlobUrl(currentPlan.oai_source_document_id)}
                        target="_blank" rel="noreferrer"
                        style={{ marginLeft: 'var(--s8)', fontSize: 'var(--t-11)', color: 'var(--accent)' }}
                      >
                        {t('adjust.pdfLink')}
                      </a>
                    )}
                  </Row>
                  {Array.isArray(currentPlan.qualitative_objectives) && currentPlan.qualitative_objectives.length > 0 && (
                    <div style={{ marginTop: 'var(--s8)' }}>
                      <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', fontWeight: 500, marginBottom: 'var(--s2)' }}>{t('adjust.objectivesLabel')}</div>
                      <ul style={{ margin: 0, paddingLeft: 'var(--s16)', fontSize: 'var(--t-12)' }}>
                        {currentPlan.qualitative_objectives.map((o, i) => <li key={i}>{o}</li>)}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>
                  {t('adjust.noPlanExtracted')}
                </div>
              )}
            </div>

            {/* Nouveau plan (form) */}
            <div style={{
              border: '1.5px solid var(--accent)',
              borderRadius: 'var(--r1)',
              padding: 'var(--s12)',
              background: 'var(--surface)',
            }}>
              <div style={{ fontSize: 'var(--t-11)', color: 'var(--accent)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 'var(--s8)' }}>
                {t('adjust.newPlanLabel')}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s8)' }}>
                <label style={{ fontSize: 'var(--t-12)', display: 'flex', alignItems: 'center', gap: 'var(--s4)', cursor: 'pointer' }}>
                  <input
                    type="radio" name="ptype" checked={isProgressive}
                    onChange={() => setProgressionType('progressive')}
                  />
                  {t('adjust.typeProgressive')}
                </label>
                <label style={{ fontSize: 'var(--t-12)', display: 'flex', alignItems: 'center', gap: 'var(--s4)', cursor: 'pointer' }}>
                  <input
                    type="radio" name="ptype" checked={!isProgressive}
                    onChange={() => setProgressionType('constant')}
                  />
                  {t('adjust.typeConstant')}
                </label>
              </div>

              {isProgressive && (
                <FormRow label={t('adjust.labelStartRate')}>
                  <NumInput value={startPct} onChange={setStartPct} min={0} max={100} suffix="%" />
                </FormRow>
              )}
              <FormRow label={isProgressive ? t('adjust.labelTargetRate') : t('adjust.labelRate')}>
                <NumInput value={targetPct} onChange={setTargetPct} min={0} max={100} suffix="%" />
              </FormRow>
              <FormRow label={t('adjust.labelPeriodStart')}>
                <DateInput value={periodStart} onChange={(v) => setPeriodStart(snapToMonday(v))} />
              </FormRow>
              <FormRow label={t('adjust.labelDeadline')}>
                <DateInput value={targetDate} onChange={setTargetDate} />
              </FormRow>
              <FormRow label={t('adjust.labelEffectiveDate')}>
                <DateInput value={effectiveFrom} onChange={(v) => setEffectiveFrom(snapToMonday(v))} />
              </FormRow>
              <div style={{
                fontSize: 'var(--t-10)',
                color: 'var(--ink-3)',
                fontStyle: 'italic',
                marginTop: 'var(--s4)',
                paddingLeft: 'var(--s4)',
              }}>
                {t('adjust.mondaySnapHint')}
              </div>

              <div style={{ marginTop: 'var(--s8)' }}>
                <label style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', display: 'block', marginBottom: 'var(--s2)' }}>
                  {t('adjust.qualitativeObjectivesLabel')}
                </label>
                <textarea
                  value={qualitativeText}
                  onChange={(e) => setQualitativeText(e.target.value)}
                  rows={3}
                  placeholder={t('adjust.qualitativeObjectivesPlaceholder')}
                  style={textareaStyle()}
                />
              </div>
            </div>
          </div>

          {/* Reason + OAI note */}
          <div style={{ marginBottom: 'var(--s12)' }}>
            <label style={{ fontSize: 'var(--t-12)', fontWeight: 600, display: 'block', marginBottom: 'var(--s2)' }}>
              {t('adjust.reasonRequiredLabel')} <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t('adjust.reasonPlaceholder')}
              style={textareaStyle()}
            />
            {!reasonOk && reason.length > 0 && (
              <div style={{ fontSize: 'var(--t-11)', color: 'var(--err)', marginTop: 'var(--s2)' }}>
                {t('adjust.reasonMinLength')}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 'var(--s12)' }}>
            <label style={{ fontSize: 'var(--t-12)', fontWeight: 600, display: 'block', marginBottom: 'var(--s2)' }}>
              {t('adjust.oaiNoteLabel')}
            </label>
            <textarea
              value={oaiNote}
              onChange={(e) => setOaiNote(e.target.value)}
              rows={2}
              style={textareaStyle()}
            />
          </div>

          {/* Recipient override */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 'var(--s8)', marginBottom: 'var(--s12)',
          }}>
            <div>
              <label style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', display: 'block', marginBottom: 'var(--s2)' }}>
                {t('adjust.recipientNameLabel')}
              </label>
              <input
                value={toName}
                onChange={(e) => setToName(e.target.value)}
                placeholder={t('adjust.recipientNamePlaceholder')}
                style={inputStyle()}
              />
            </div>
            <div>
              <label style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', display: 'block', marginBottom: 'var(--s2)' }}>
                {t('adjust.recipientEmailLabel')}
              </label>
              <input
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder={t('adjust.recipientEmailPlaceholder')}
                type="email"
                style={inputStyle()}
              />
            </div>
          </div>

          {/* Preview */}
          <div style={{
            border: '1px solid var(--line)',
            borderRadius: 'var(--r1)',
            overflow: 'hidden',
          }}>
            <button
              type="button"
              onClick={() => setShowPreview(s => !s)}
              style={{
                width: '100%', textAlign: 'left',
                padding: 'var(--s8) var(--s12)',
                background: 'var(--bg)',
                border: 'none', cursor: 'pointer',
                fontSize: 'var(--t-12)', fontWeight: 600,
                color: 'var(--ink-1)',
              }}
            >
              {showPreview ? '▼' : '▸'} {t('adjust.previewToggle')}
            </button>
            {showPreview && (
              <div style={{ padding: 'var(--s12)', borderTop: '1px solid var(--line)' }}>
                <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)', marginBottom: 'var(--s4)' }}>
                  <strong>{t('adjust.subjectLabel')}</strong> {preview.subject}
                </div>
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                  fontSize: 'var(--t-11)',
                  color: 'var(--ink-1)',
                  margin: 0,
                  background: 'var(--bg)',
                  padding: 'var(--s8)',
                  borderRadius: 'var(--r2)',
                }}>{preview.body}</pre>
              </div>
            )}
          </div>

          {error && (
            <div style={{
              marginTop: 'var(--s8)',
              padding: 'var(--s8)',
              background: 'var(--err-dim)',
              color: 'var(--err)',
              borderRadius: 'var(--r2)',
              fontSize: 'var(--t-12)',
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer style={{
          padding: 'var(--s12) var(--s16)',
          borderTop: '1px solid var(--line)',
          display: 'flex', justifyContent: 'flex-end',
          gap: 'var(--s8)',
        }}>
          <button onClick={onClose} className="sort-btn">{t('common.cancel')}</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              padding: 'var(--s6) var(--s12)',
              borderRadius: 'var(--r1)',
              border: 'none',
              background: canSubmit ? 'var(--accent)' : 'var(--ink-3)',
              color: 'var(--surface)',
              fontSize: 'var(--t-12)',
              fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? t('common.saving') : t('adjust.submitButton')}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--s8)', fontSize: 'var(--t-12)', marginBottom: 'var(--s2)' }}>
      <span style={{ color: 'var(--ink-3)', minWidth: 80 }}>{label}</span>
      <span style={{ color: 'var(--ink-1)' }}>{children}</span>
    </div>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s4)' }}>
      <span style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)', minWidth: 100 }}>{label}</span>
      <span style={{ flex: 1 }}>{children}</span>
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    width: '100%',
    padding: 'var(--s4) var(--s8)',
    borderRadius: 'var(--r2)',
    border: '1px solid var(--line)',
    fontSize: 'var(--t-12)',
    background: 'var(--surface)',
    color: 'var(--ink-1)',
  };
}

function textareaStyle(): React.CSSProperties {
  return {
    width: '100%',
    padding: 'var(--s8)',
    borderRadius: 'var(--r2)',
    border: '1px solid var(--line)',
    fontSize: 'var(--t-12)',
    background: 'var(--surface)',
    color: 'var(--ink-1)',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  };
}

function NumInput({
  value, onChange, min, max, suffix,
}: { value: string; onChange: (v: string) => void; min: number; max: number; suffix?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s4)' }}>
      <input
        type="number" value={value} onChange={(e) => onChange(e.target.value)}
        min={min} max={max} step={1}
        style={{ ...inputStyle(), width: 90 }}
      />
      {suffix && <span style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{suffix}</span>}
    </span>
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="date" value={value} onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle(), width: 160 }}
    />
  );
}
